import {
    imageTransformationsToString,
    ProfilePictureMedia,
} from '@renderer-ui-library/molecules';
import dayjs from 'dayjs';
import { SetOptional } from 'type-fest';

export enum ZohoUserAssignmentPageKey {
    AboutUs = 'About-us',
    Auction = 'Auction',
    BuyingAbroad = 'Buying-Abroad',
    HappyCustomers = 'Happy-Customers',
    Inspection = 'Inspection',
    Payment = 'Payment',
    ProductDetails = 'Product-Details',
    Startpage = 'Startpage',
    Transport = 'Transport',
    Warranty = 'Warranty',
    CountryTeam = 'Country-Team',
}

export type ZohoUserAssignment = {
    rank: number | null;
    pageKey: ZohoUserAssignmentPageKey;
};

export type TSalesManager = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    languagesSpoken: string[];
    isSalesManager: boolean;
    isBusinessSpecialist: boolean;
    jobTitle: string;
    bioTitle: string;
    bioText: string;
    zohoUserId: string;
    assignments: ZohoUserAssignment[];
    profilePictureIndoorsTimestamp: string | null;
    profilePictureOutdoorsTimestamp: string | null;
    profilePictureAvatarTimestamp: string | null;
};

const hasZohoUserAssignmentFilter =
    (pageKey: ZohoUserAssignmentPageKey) => (salesManager: TSalesManager) =>
        salesManager.assignments.some(
            assignment => assignment.pageKey === pageKey,
        );

export const getAssignedSalesManagers = (
    salesManagers: TSalesManager[],
    pageKey: ZohoUserAssignmentPageKey,
) => {
    return salesManagers
        .filter(hasZohoUserAssignmentFilter(pageKey))
        .sort((a, b) => {
            const aAssignment = a.assignments.find(
                assign => assign.pageKey === pageKey,
            );
            const bAssignment = b.assignments.find(
                assign => assign.pageKey === pageKey,
            );

            const aRank = aAssignment?.rank;
            const bRank = bAssignment?.rank;

            if (typeof aRank === 'number' && typeof bRank === 'number') {
                return aRank - bRank;
            } else if (typeof aRank === 'number' && bRank == null) {
                return -1;
            } else if (aRank == null && typeof bRank === 'number') {
                return 1;
            } else if (aRank == null && bRank == null) {
                // If both are null, sort by firstName
                return a.firstName.localeCompare(b.firstName);
            }

            return 0;
        });
};

export const getProfilePictureTimestamp = (
    salesManager: TSalesManager,
    variant: 'indoors' | 'outdoors' | 'avatar',
) => {
    let ts: string | null | undefined;
    switch (variant) {
        case 'indoors':
            ts = salesManager.profilePictureIndoorsTimestamp;
            break;
        case 'outdoors':
            ts = salesManager.profilePictureOutdoorsTimestamp;
            break;
        case 'avatar':
            ts = salesManager.profilePictureAvatarTimestamp;
            break;
    }

    return ts ?? null;
};

export const getProfilePictureUrl = (
    media: SetOptional<ProfilePictureMedia, 'name' | 'type'>,
) => {
    const queryParams = new URLSearchParams({
        updatedAt: media.timestamp ?? dayjs().format('YYYY-MM-DD'),
    });
    const transformations = imageTransformationsToString(
        media.transformations ?? {},
    );
    if (transformations) {
        queryParams.append('tr', transformations);
    }

    return `https://ik.imagekit.io/efarm/user-profile-picture/${media.zohoUserId}/${media.variant}?${queryParams.toString()}`;
};

export const getName = (salesManager: TSalesManager) =>
    `${salesManager.firstName} ${salesManager.lastName}`.trim();
